import {
  apiUrl,
  getList,
  getSingleRecord,
  saveRecord,
  deleteRecord,
  putAction,
  get,
  download,
  upload,
  uploadWithMethod,
  getAuditData,
  postAction,
  handleResponse,
  deleteAction,
  exportData,
  buildScopeQueryString,
} from './apiUtils';
import type {
  ApiListParamsWithUnderscores,
  ApiExportParams,
} from '../types/api/listParams';
import type { ListApiResponse } from '../types/apiResponse';

const baseUrl = apiUrl + '/requests/';
const exportBaseUrl = `${apiUrl}/export/requests/`;

export type RequestListParams = ApiListParamsWithUnderscores & {
  assignedToMe?: boolean;
};

export function getRequestsGrouped(
  params: RequestListParams
): Promise<ListApiResponse<unknown>> {
  if (params.assignedToMe) {
    return getList(`${baseUrl}assignedToMe/grouped`, params);
  }
  return getList(`${baseUrl}grouped`, params);
}

export function getRequests(
  params: RequestListParams
): Promise<ListApiResponse<unknown>> {
  if (params.assignedToMe) {
    return getList(`${baseUrl}assignedToMe`, params);
  }
  return getList(`${baseUrl}search`, params);
}

export function getRequestsMadeByMe(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}mine`, params);
}

export function getRequestsMadeByMeGrouped(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}mine/grouped`, params);
}

export function getRequestById(id: string): Promise<unknown> {
  return getSingleRecord(`${baseUrl}group/`, id);
}

export function getRequestByKeyRequestGroupId(id: string): Promise<unknown> {
  return get(`${baseUrl}group/${id}/requests`);
}

export function saveRequest(request: unknown): Promise<unknown> {
  return saveRecord(baseUrl, request);
}

export function deleteRequestGroup(id: string): Promise<unknown> {
  return deleteRecord(`${baseUrl}group/`, id);
}

export function submitRequestGroup(
  id: string,
  record: unknown
): Promise<unknown> {
  return uploadWithMethod(`${baseUrl}group/${id}/submit`, record, 'PUT');
}

export function deleteRequest(
  requestGroupId: string,
  id: string
): Promise<unknown> {
  return deleteRecord(`${baseUrl}group/${requestGroupId}/request/`, id);
}

export function getRequestsToBeApproved(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}approvals`, params);
}

export function getRequestsToBeApprovedGrouped(
  params?: ApiListParamsWithUnderscores
): Promise<ListApiResponse<unknown>> {
  return getList(`${baseUrl}approvals/grouped`, params);
}

export function reassignRequest(
  requestId: string,
  assignToUserId?: string
): Promise<unknown> {
  if (assignToUserId) {
    return putAction(`${baseUrl}${requestId}/reassign/${assignToUserId}`);
  }
  return putAction(`${baseUrl}${requestId}/reassign/`);
}

export function changeStatus(
  requestId: string,
  statusId: string,
  reason: string
): Promise<unknown> {
  return putAction(`${baseUrl}bulkchangestatus`, {
    requestIds: [requestId],
    statusId,
    reason,
  });
}

export function changeStatuses(
  requestIds: string[],
  statusId: string,
  reason: string
): Promise<unknown> {
  return putAction(`${baseUrl}bulkchangestatus`, {
    requestIds,
    statusId,
    reason,
  });
}

export function getAvailableStatuses(): Promise<unknown> {
  return get(`${baseUrl}availablestatuses`);
}

export function getAvailableStatusesForRequest(
  requestId: string
): Promise<unknown> {
  return get(`${baseUrl}${requestId}/availablestatuses`);
}

export function withdrawRequest(requestId: string): Promise<unknown> {
  return putAction(`${baseUrl}${requestId}/withdraw`, null);
}

export function updateRequestValue(
  requestId: string,
  value: unknown,
  reason: string
): Promise<unknown> {
  return putAction(`${baseUrl}${requestId}/value`, {
    value,
    reason,
  });
}

export function updateNumberOfKeys(
  requestId: string,
  numberOfKeys: number,
  reason: string
): Promise<unknown> {
  return putAction(`${baseUrl}${requestId}/numberofkeys`, {
    numberOfKeys,
    reason,
  });
}

export function downloadKeys(requestId: string): Promise<void> {
  return download(`${baseUrl}${requestId}/downloadkeys`);
}

export function getApprovalStatus(
  requestGroupId: string,
  requestId: string
): Promise<unknown> {
  return get(`${baseUrl}group/${requestGroupId}/${requestId}/status`);
}

export function getApprovals(
  requestGroupId: string,
  requestId: string
): Promise<unknown> {
  return get(`${baseUrl}group/${requestGroupId}/${requestId}/approvals`);
}

export function uploadKeys(
  requestId: string,
  record: unknown
): Promise<unknown> {
  return upload(`${baseUrl}${requestId}/uploadKeys`, record);
}

export function getBasket(): Promise<unknown> {
  return get(`${baseUrl}basket`);
}

export function checkRequestTransactionLimits(
  requestId: string,
  overrideRequestValue?: string
): Promise<unknown> {
  return get(
    `${baseUrl}${requestId}/checktransactionlimits${
      overrideRequestValue
        ? '?overrideRequestValue=' + encodeURIComponent(overrideRequestValue)
        : ''
    }`
  );
}

export function checkRequestGroupTransactionLimits(
  requestGroupId: string
): Promise<unknown> {
  return get(`${baseUrl}group/${requestGroupId}/checktransactionlimits`);
}

export function approveRequest(
  requestId: string,
  signature: string,
  overrideRequestValue?: string
): Promise<unknown> {
  return putAction(`${baseUrl}${requestId}/sign`, {
    action: 'approve',
    signature,
    overrideRequestValue,
  });
}

export function declineRequest(
  requestId: string,
  declineReason: string
): Promise<unknown> {
  return putAction(`${baseUrl}${requestId}/sign`, {
    action: 'decline',
    declineReason,
  });
}

export function getAudit(id: string): Promise<unknown> {
  return getAuditData(`${baseUrl}group/`, id);
}

export function addAudit(id: string, comment: string): Promise<unknown> {
  return postAction(`${baseUrl}group/${id}/audit`, { comment });
}

export function downloadAccountDetailsTemplate(): Promise<void> {
  return download(`${baseUrl}download/accountDetailsTemplate`);
}

export function uploadAccountDetails(
  id: string,
  record: unknown
): Promise<unknown> {
  return uploadWithMethod(
    `${baseUrl}${id}/upload/accountDetails`,
    record,
    'POST'
  );
}

export function getAccountDetails(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/accountDetails`);
}

export function saveAccountDetails(
  id: string,
  record: unknown
): Promise<unknown> {
  return postAction(`${baseUrl}${id}/accountDetails`, record);
}

export function deleteAccountDetail(
  requestId: string,
  id: string
): Promise<unknown> {
  return deleteAction(`${baseUrl}${requestId}/accountDetails/${id}`);
}

export function sendAccountDetailEmail(
  requestId: string,
  id: string
): Promise<unknown> {
  return postAction(`${baseUrl}${requestId}/accountDetails/${id}/sendemail`);
}

export function downloadAccountDetailKeyCode(
  requestId: string,
  accountId: string
): Promise<unknown> {
  return fetch(
    `${baseUrl}${requestId}/accountDetails/${accountId}/download`
  ).then(handleResponse);
}

export function copyRequestGroup(
  id: string,
  copyDetails: unknown
): Promise<unknown> {
  return postAction(`${baseUrl}group/${id}/copy`, {
    copyRequests: copyDetails,
  });
}

export function bulkSaveRequests(requests: unknown): Promise<unknown> {
  return postAction(`${baseUrl}bulk`, requests);
}

export function exportRequestData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}search`, 'reference', params);
}

export function exportMyRequestData(params: ApiExportParams): Promise<void> {
  return exportData(`${exportBaseUrl}mine`, 'reference', params);
}

export function exportAssignedRequestData(
  params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}assignedToMe`, 'reference', params);
}

export function exportApprovalRequestData(
  params: ApiExportParams
): Promise<void> {
  return exportData(`${exportBaseUrl}approvals`, 'reference', params);
}

export function validateCodeFile(
  keyRequestGroupId: string,
  filename: string
): Promise<unknown> {
  return postAction(`${baseUrl}group/${keyRequestGroupId}/upload/validate`, {
    filename,
  });
}

export function downloadPurchaseOrder(
  keyRequestGroupId: string,
  fileId: string
): Promise<void> {
  return download(
    `${baseUrl}group/${keyRequestGroupId}/download?fileId=${fileId}`
  );
}

export function getStatsByDay(): Promise<unknown> {
  return get(`${baseUrl}statsByDay/`);
}

export function getAggregateStats(from: Date, to: Date): Promise<unknown> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];
  return get(`${baseUrl}aggregateStats?fromDate=${fromStr}&toDate=${toStr}`);
}

export function getAggregateStatsByScopes(
  scopes: Record<string, string>,
  from: Date,
  to: Date
): Promise<unknown> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];

  let url = `${baseUrl}aggregateStatsByScopes?fromDate=${fromStr}&toDate=${toStr}`;

  const scopesQuery = buildScopeQueryString(scopes ?? {});
  if (scopesQuery) url += `&${scopesQuery}`;

  return get(url);
}

export function getRecentRequestsByScopes(
  scopes: Record<string, string>,
  from: Date,
  to: Date
): Promise<ListApiResponse<unknown>> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];

  let url = `${baseUrl}recentRequestsByScopes?_page=0&_limit=10&_sort=requestedDate&_order=desc&requestedDateFrom_like=${fromStr}&requestedDateTo_like=${toStr}`;

  const scopesQuery = buildScopeQueryString(scopes ?? {});
  if (scopesQuery) url += `&${scopesQuery}`;

  return get(url);
}

export function exportAggregateStats(
  from: Date,
  to: Date,
  type: string
): Promise<void> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];
  return download(
    `${exportBaseUrl}exportAggregateStats?fromDate=${fromStr}&toDate=${toStr}&type=${type}`
  );
}

export function exportAggregateStatsByScopes(
  scopes: Record<string, string>,
  from: Date,
  to: Date,
  type: string
): Promise<void> {
  const fromStr = from.toISOString().split('T')[0];
  const toStr = to.toISOString().split('T')[0];

  let url = `${exportBaseUrl}exportAggregateStatsByScopes?fromDate=${fromStr}&toDate=${toStr}&type=${type}`;

  const scopesQuery = buildScopeQueryString(scopes ?? {});
  if (scopesQuery) url += `&${scopesQuery}`;

  return download(url);
}

export function getCodeStatsByRequestForRequest(id: string): Promise<unknown> {
  return get(`${baseUrl}${id}/codeStats`);
}

export function returnKeys(requestId: string): Promise<unknown> {
  return postAction(`${baseUrl}${requestId}/returnKeys`);
}
